import { KanbanTag, MultiSelect } from '@evogard/ui';
import type { UseQueryResult } from '@tanstack/react-query';
import type { AppliedLabel, DepartmentLabels } from '@evogard/contract';

/*
 * As etiquetas de card, sem saber de quem é o card (27/10/2026). Nasceram na
 * mesa do sinistro (`modules/claims/claim/ClaimLabels.tsx`) e subiram para cá
 * quando a mesa da despesa do Financeiro passou a ter etiquetas: o catálogo
 * é o mesmo (`app.labels`, de cada setor), e um módulo não importa o outro.
 * Quem decide se a pessoa pode pôr é quem monta a mesa (`editable`).
 */

/**
 * As etiquetas que o card tem, como selos de cor — no cartão do quadro, na
 * tabela e na mesa. São as dos setores de quem olha; o setor vai no `title`.
 */
export function LabelTags({ labels }: { labels: readonly AppliedLabel[] }) {
  if (labels.length === 0) return null;
  return (
    <>
      {labels.map((l) => (
        <KanbanTag key={l.id} color={l.color} title={`${l.name} · ${l.departmentName}`}>
          {l.name}
        </KanbanTag>
      ))}
    </>
  );
}

/** A consulta das etiquetas dos setores de quem usa — pela rota do card. */
export type LabelOptions = UseQueryResult<DepartmentLabels[], Error>;

/** Gravar a escolha do seletor. */
export interface SaveLabels {
  mutate(labelIds: number[]): void;
  isPending: boolean;
  isError: boolean;
  error: Error | null;
}

/**
 * Os selos e o seletor. Marcar e desmarcar gravam na hora — etiqueta não é
 * rascunho do formulário. Quem não pode pôr vê os selos, sem a pastilha; quem
 * não está em setor nenhum, ou cujo setor não tem etiqueta, vê a pastilha
 * desabilitada com o motivo no `title`.
 */
export function LabelsPicker({
  labels,
  editable,
  save,
  options,
}: {
  labels: readonly AppliedLabel[];
  /** A pessoa pode pôr e tirar, e o card não está arquivado. */
  editable: boolean;
  save: SaveLabels;
  options: LabelOptions;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <LabelTags labels={labels} />
      {editable && <AddLabels labels={labels} save={save} options={options} />}
    </div>
  );
}

function AddLabels({
  labels,
  save,
  options,
}: {
  labels: readonly AppliedLabel[];
  save: SaveLabels;
  options: LabelOptions;
}) {
  const sectors = options.data ?? [];

  /* As que o card tem e saíram do catálogo continuam marcadas, e saem se desmarcadas. */
  const offered = new Set(sectors.flatMap((s) => s.labels.map((l) => l.id)));
  /* O setor só entra no nome quando dois setores da pessoa têm a mesma etiqueta. */
  const names = sectors.flatMap((s) => s.labels.map((l) => l.name));
  const repeated = (name: string) => names.filter((n) => n === name).length > 1;
  const choices = [
    ...sectors.flatMap((s) =>
      s.labels.map((l) => ({
        value: String(l.id),
        label: repeated(l.name) ? `${l.name} · ${s.departmentName}` : l.name,
      })),
    ),
    ...labels
      .filter((l) => !offered.has(l.id))
      .map((l) => ({ value: String(l.id), label: `${l.name} (arquivada)` })),
  ];
  const empty = options.data !== undefined && choices.length === 0;
  const title = options.isError
    ? options.error.message
    : save.isError
      ? (save.error?.message ?? 'Não foi possível gravar as etiquetas.')
      : empty
        ? sectors.length === 0
          ? 'Você não está em nenhum departamento, e as etiquetas são de cada um.'
          : 'O seu departamento ainda não tem etiquetas — elas se montam em Departamentos.'
        : labels.length === 0
          ? 'Adicionar etiqueta'
          : 'Adicionar ou tirar etiquetas';

  return (
    <MultiSelect
      label="Etiquetas"
      options={choices}
      selected={labels.map((l) => String(l.id))}
      onChange={(next) => save.mutate(next.map(Number))}
      searchPlaceholder="Buscar etiqueta"
      emptyMessage="Nenhuma etiqueta com esse nome."
      loading={options.isLoading}
      disabled={empty}
      layer="modal"
      compact={{ icon: 'etiqueta', title, loading: save.isPending }}
    />
  );
}
